import { useState } from "react";
import { Link } from "react-router-dom";
import { entityTrack } from "@/lib/entities";

/* Latest Coverage — canonical entity relevance only (Figma contract).
   Adaptive geometry: composition follows real inventory; hides at zero.
   Cover media follows the shared article visual pipeline: canonical article
   visual (hero_image_url, same field Homepage/Newsstream renders) first;
   NewsLayer deterministic dot-field artwork only when media is missing or
   fails to load. Reserved geometry on both breakpoints — zero CLS. */

const relTime = (iso?: string) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.round(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const contextFor = (a: any, tokenSymbol: string | null, chains: string[]) => {
  if (tokenSymbol && new RegExp(`\\b${tokenSymbol}\\b`).test(a.title || ""))
    return { label: "MARKET", color: "#7a3dff" };
  const chain = chains.find((c) => new RegExp(`\\b${c}\\b`, "i").test(a.title || ""));
  if (chain) return { label: "DEPLOYMENT", color: "#05d1f0" };
  if ((a.categories || []).includes("defi")) return { label: "DEFI", color: "#05d1f0" };
  return { label: "PROTOCOL", color: "#7a3dff" };
};

const CoverArt = ({ accent }: { accent: string }) => (
  <>
    <svg viewBox="0 0 272 150" preserveAspectRatio="xMidYMid slice" aria-hidden
      className="hidden sm:block w-full h-[150px] rounded-[18px]"
      style={{ background: "linear-gradient(135deg,#1a0a38 0%,#081429 55%,#03050a 100%)" }}>
      {[{ x: 45, y: 57, r: 5, o: 0.15 }, { x: 80, y: 89, r: 8.5, o: 0.23 }, { x: 116, y: 56, r: 12, o: 0.31 },
        { x: 151, y: 88, r: 15.5, o: 0.39 }, { x: 187, y: 63, r: 19, o: 0.47 }].map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={c.r} fill={accent} opacity={c.o} />
      ))}
    </svg>
    <svg viewBox="0 0 84 170" preserveAspectRatio="xMidYMid slice" aria-hidden
      className="sm:hidden w-[84px] self-stretch rounded-[14px] shrink-0"
      style={{ background: "linear-gradient(160deg,#1f0a40 0%,#030a14 100%)" }}>
      {[{ x: 25, y: 55, r: 7, o: 0.22 }, { x: 47, y: 85, r: 11, o: 0.32 }, { x: 34, y: 118, r: 15, o: 0.42 }].map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={c.r} fill={accent} opacity={c.o} />
      ))}
    </svg>
  </>
);

const CoverMedia = ({ src, accent }: { src?: string | null; accent: string }) => {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return <CoverArt accent={accent} />;
  return (
    <>
      <div className="hidden sm:block w-full h-[150px] rounded-[18px] overflow-hidden bg-[#0a0a12]">
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)}
          className="w-full h-full object-cover" data-testid="coverage-card-media" />
      </div>
      <div className="sm:hidden w-[84px] self-stretch rounded-[14px] overflow-hidden shrink-0 bg-[#0a0a12]">
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)}
          className="w-full h-full object-cover" />
      </div>
    </>
  );
};

export const EntityCoverage = ({ overview }: { overview: any }) => {
  const articles = overview.coverage?.articles || [];
  if (!articles.length) return null;
  const name = overview.identity.name;
  const chains = (overview.chain_distribution?.chains || []).map((c: any) => c.chain);
  const n = articles.length;
  const cols = n >= 4 ? "sm:grid-cols-2 xl:grid-cols-4" : n === 3 ? "sm:grid-cols-3" : n === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1 sm:max-w-[420px]";
  return (
    <section data-testid="entity-coverage">
      <p className="nl-geist text-[9px] md:text-[10px] lg:text-[11px] leading-[14px] font-semibold text-[#7a3dff] tracking-[0.02em]">NEWSLAYER COVERAGE</p>
      <h2 className="nl-sg text-[27px] leading-[35px] md:text-[31px] md:leading-[40px] lg:text-[34px] lg:leading-[43px] font-medium text-[#f6f7fc] mt-[8px]">
        Latest {name} Coverage
      </h2>
      <p className="nl-geist hidden lg:block text-[15px] leading-[19px] text-[#9499ad] mt-[6px]">
        Only stories with canonical {name} relevance enter this section.
      </p>
      <p className="nl-geist lg:hidden text-[12px] md:text-[14px] leading-[16px] md:leading-[18px] text-[#9499ad] mt-[6px]">
        Canonical {name} relevance only.
      </p>
      <div className={`mt-[22px] grid grid-cols-1 gap-[14px] lg:gap-[32px] ${cols}`}>
        {articles.map((a: any, i: number) => {
          const ctx = contextFor(a, overview.token?.symbol || null, chains.slice(0, 6));
          return (
            <Link key={a.slug} to={`/article/${a.slug}`}
              onClick={() => entityTrack("article_click", overview.identity.entity_id, { slug: a.slug, position: i })}
              className="group flex sm:block gap-[14px] rounded-[16px] sm:rounded-[24px] bg-[#07070a] border border-[#1a1c2b] p-[14px] sm:p-[16px] hover:border-[#2b2e45] transition-colors duration-200"
              data-testid={`coverage-card-${i}`}>
              <CoverMedia src={a.hero_image_url} accent={ctx.color} />
              <div className="min-w-0 flex flex-col flex-1">
                <p className="nl-geist text-[8px] sm:text-[10px] leading-[13px] font-semibold mt-[2px] sm:mt-[16px]"
                  style={{ color: ctx.color }}>{ctx.label}</p>
                <h3 className="nl-sg text-[15px] leading-[19px] sm:text-[18px] sm:leading-[23px] font-medium text-[#f6f7fc] mt-[8px] line-clamp-3">
                  {a.title}
                </h3>
                <p className="nl-mono text-[8px] sm:text-[11px] leading-[14px] font-medium text-[color:var(--nl-text-faint)] mt-auto pt-[18px] sm:pt-[26px]">
                  NewsLayer · {relTime(a.published_at)}
                </p>
                <p className="nl-geist text-[9px] sm:text-[11px] leading-[14px] font-semibold mt-[14px] sm:mt-[18px] group-hover:translate-x-[2px] transition-transform duration-200"
                  style={{ color: ctx.color }}>READ STORY →</p>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
